import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@unboared/react';

import { LoadingPage } from '~/pages/LoadingPage';

import styles from './SessionClosedOverlay.module.css';

// Server onError.infos that mean the session we tried to (re)join is gone.
const DEAD_SESSION_INFOS = ['Session id does not exist', 'Device no longer in session'];

// Grace after an explicit dead-session error: the lib may still recover via a
// fallback joinSession (e.g. our device's grace expired but the session is alive).
const ERROR_GRACE_MS = 4000;

// Backstop grace after the socket connects with no `ready`. Level-triggered, so
// it can't be missed even if the dead-session onError fired before our listener
// attached (socket.io does not replay events). Generous so a slow-but-live join
// is not misflagged; a late `ready` cancels it (and unmounts the loader anyway).
const CONNECT_GRACE_MS = 7000;

// Never connected at all within this window => the phone cannot reach the
// server (venue Wi-Fi / corporate network filtering the socket domain — same
// incident as ScreenConnectLoader, 2026-09-15). Generous so a slow first
// handshake is not misflagged; a late `connect` cancels it.
const UNREACHABLE_MS = 20000;

/**
 * Loader shown by `UnboaredGame` (gamepad) until the session is ready.
 *
 * Normally it just shows the "connecting" spinner. But when the gamepad reloads
 * onto a session that the server has already killed, the (re)join fails with
 * "Session id does not exist" and `ready` never fires — leaving the spinner
 * forever, with no hint that the player must rescan. This component listens to
 * the raw socket and, after a short grace window with no `ready`, swaps the
 * spinner for the "session closed" screen. A late `ready` (slow join) simply
 * unmounts the loader, so the swap can never get stuck over a live session.
 */
export function GamepadLoader() {
    const { t } = useTranslation();
    const [dead, setDead] = useState(false);
    const [unreachable, setUnreachable] = useState(false);

    useEffect(() => {
        let ready = false;
        let connected = false;
        let deadTimer: ReturnType<typeof setTimeout> | undefined;
        let poll: ReturnType<typeof setInterval> | undefined;
        let wss: any;

        // True once we have actually joined a session. Distinguishes the initial
        // join attempt (failure => dead) from a harmless loader remount on an
        // already-live session (UnboaredGame re-shows the loader briefly while its
        // state managers initialize) — we must not flag the latter as dead.
        const alreadyJoined = () => {
            try {
                return Boolean((api() as any)?.getSessionID?.());
            } catch {
                return false;
            }
        };

        const unreachableTimer = setTimeout(() => {
            if (!connected && !ready && !alreadyJoined()) setUnreachable(true);
        }, UNREACHABLE_MS);

        const armDead = (ms: number) => {
            if (ready || alreadyJoined()) return;
            if (deadTimer) clearTimeout(deadTimer);
            deadTimer = setTimeout(() => {
                if (!ready) setDead(true);
            }, ms);
        };

        // Connected but no `ready` within the window => the session is gone. Only
        // arm if nothing is pending: a socket.io auto-reconnect (flaky phone data)
        // fires `connect` again, and re-arming here would clear a shorter, already
        // running ERROR_GRACE timer and defer the dead screen indefinitely.
        const onConnect = () => {
            connected = true;
            if (!deadTimer) armDead(CONNECT_GRACE_MS);
        };
        // Explicit dead-session error => confirm faster (still graced for the
        // lib's transient resume -> joinSession recovery on a live session).
        const onError = (err: any) => {
            if (DEAD_SESSION_INFOS.includes((err && err.infos) || '')) armDead(ERROR_GRACE_MS);
        };
        // A real `ready` cancels a pending timer. We do NOT un-set `dead` once it is
        // shown: a genuine late `ready` unmounts this whole loader anyway (UnboaredGame
        // becomes ready), so there is no need to flash the spinner back over the
        // actionable "session closed / rescan" screen.
        const onReady = () => {
            ready = true;
            if (deadTimer) clearTimeout(deadTimer);
        };

        const attach = () => {
            const a: any = api();
            wss = a?.getWSS?.() ?? a?.wss;
            if (!wss) return false;
            wss.on('connect', onConnect);
            wss.on('onError', onError);
            wss.on('ready', onReady);
            // The socket may already be connected by the time we attach.
            if (wss.connected) onConnect();
            return true;
        };

        // The api/socket is created in UnboaredGame's effect, which runs after this
        // loader mounts — poll briefly until the socket exists.
        if (!attach()) {
            poll = setInterval(() => {
                if (attach() && poll) clearInterval(poll);
            }, 30);
        }

        return () => {
            if (poll) clearInterval(poll);
            if (deadTimer) clearTimeout(deadTimer);
            clearTimeout(unreachableTimer);
            if (wss) {
                wss.off('connect', onConnect);
                wss.off('onError', onError);
                wss.off('ready', onReady);
            }
        };
    }, []);

    if (dead) {
        return (
            <div className={styles.root}>
                <p className={styles.title}>{t('gamepad.sessionClosed.title')}</p>
                <p className={styles.hint}>{t('gamepad.sessionClosed.hint')}</p>
            </div>
        );
    }

    if (unreachable) {
        return (
            <div className={styles.root}>
                <p className={styles.title}>{t('gamepad.connectTimeout.title')}</p>
                <p className={styles.hint}>{t('gamepad.connectTimeout.hint')}</p>
            </div>
        );
    }

    return <LoadingPage>{t('home.connectServer')}</LoadingPage>;
}
